import {
	Box,
	Button,
	Icons,
	Stack,
	Component,
	Divider,
	Inline,
	Container,
	Badge,
	joinAttributes,
} from "@sembark-travel/ui-base"
import { useXHR } from "@sembark-travel/xhr"
import { useAuthUser } from "./../Auth"
import { NewImageMediaForm } from "./../Media"
import { PhoneNumber } from "../Contacts"
import { EmailSignatureEditor } from "./EmailSignatureEditor"

export default function Profile() {
	const { user, fetchUser } = useAuthUser()
	const xhr = useXHR()
	if (!user) {
		return (
			<Box padding="3">
				Can not retrieve the profile at the moment. Please try after some time.
			</Box>
		)
	}
	const { id, tenant, profile_image_url } = user
	return (
		<Container paddingY="4">
			<Inline padding="4" flexWrap="wrap" gap="3">
				{profile_image_url ? (
					<Box position="relative">
						<Box
							as="img"
							src={profile_image_url}
							alt="Avatar"
							style={{ width: "200px", height: "200px" }}
							borderWidth="1"
							borderColor="default"
							rounded="lg"
						/>
						<Box
							as="span"
							cursor="pointer"
							padding="2"
							width="8"
							display="flex"
							alignItems="center"
							justifyContent="center"
							height="8"
							backgroundColor="primary"
							position="absolute"
							borderRadius="md"
							borderColor="primary"
							style={{
								top: "2%",
								right: "3%",
							}}
							onClick={async () => {
								if (
									window.confirm("Are you sure you want to delete this image ?")
								) {
									try {
										await xhr.delete(`/users/${id}/profile-image`)
										await fetchUser()
									} catch (e) {
										const error = e as Error
										window.alert(error.message)
									}
								}
							}}
						>
							<Icons.Trash color="primary" />
						</Box>
					</Box>
				) : (
					<Box
						style={{ width: "200px", height: "200px" }}
						display="flex"
						borderWidth="1"
						borderColor="accent"
						rounded="lg"
						bgColor="accent"
						color="accent"
						justifyContent="center"
						alignItems="center"
					>
						<Icons.User size="20" />
					</Box>
				)}

				<Stack flex="1" gap="2">
					<Inline gap="1" alignItems="center">
						<Box fontSize="3xl" fontWeight="semibold">
							{user.name}
						</Box>

						<Badge accent>
							<Inline flexWrap="wrap">
								{joinAttributes(...(user.roles?.map((t) => t.name) || []))}
							</Inline>
						</Badge>
					</Inline>
					<Inline gap="1" alignItems="center">
						<Icons.Mail size="6" />
						<Box fontSize="base">{user.email}</Box>
					</Inline>
					{user.phone_numbers?.length ? (
						<Box fontSize="base">
							<PhoneNumber color={"black"} value={user.phone_numbers} />
						</Box>
					) : null}

					{user.manages_users?.length ? (
						<Stack gap="1">
							<Box
								color="muted"
								fontWeight="normal"
								fontSize="base"
								letterSpacing="wider"
								textTransform="uppercase"
							>
								<Icons.Users /> Manages
							</Box>
							<Box fontSize="sm" fontWeight="semibold">
								{user.manages_users?.map((t) => t.name).join(", ")}
							</Box>
						</Stack>
					) : null}

					<Box paddingTop={"3"}>
						<Component initialState={!profile_image_url}>
							{({ state, setState }) =>
								!state ? (
									<Button status={"primary"} onClick={() => setState(true)}>
										Update Image
									</Button>
								) : (
									<NewImageMediaForm
										onCancel={() => setState(false)}
										onSubmit={async (data) => {
											await xhr.patch(`/users/${id}/profile-image`, data)
											setState(false)
											await fetchUser(false)
										}}
										help="Image must be 400x400 JPEG/PNG file with less than 1 MB in size."
										crop={{
											aspect: 1,
											minWidth: 100,
											minHeight: 100,
										}}
									/>
								)
							}
						</Component>
					</Box>

					<Divider sm />
					{tenant ? (
						<Inline paddingX={"2"} gap="1">
							<Icons.Building />

							<Stack gap="2">
								<Box as="h3" fontSize="base" fontWeight="semibold">
									Business Details
								</Box>
								<Inline gap="8">
									<Stack>
										<Box fontSize="sm" color={"muted"} fontWeight="normal">
											Business Name
										</Box>
										<Box fontSize="sm" fontWeight="semibold">
											{tenant.name}
										</Box>
									</Stack>
									<Stack>
										<Box fontSize="sm" color={"muted"} fontWeight="normal">
											Business ID
										</Box>
										<Box fontSize="sm" fontWeight="semibold">
											{tenant.identity_name}
										</Box>
									</Stack>
								</Inline>
							</Stack>
						</Inline>
					) : null}
				</Stack>
			</Inline>
			<Divider sm />
			<Box id="email-signature" padding="4">
				<EmailSignatureEditor />
			</Box>
		</Container>
	)
}
